/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { DELETE_VALUE, UPDATE_VALUE, UPLOAD_FILE } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { CREATE_VALUE } from "../graphql/createValue.mutation";

const is = require("is_js");
const log: any = LoggerModule?.getInstance();

export interface UpdateValueProps {
  formData: {
    name?: string;
    year?: string;
    status?: string;
  };
  id: string;
}
export interface CreateValueProps {
  formData: {
    alias?: string;
    description?: string;
    img1?: string;
    img2?: string;
    isVisible?: boolean;
    minDownPayment?: string;
    regMode?: string;
  };
  id: string;
}
export interface DeleteValueProps {
  id: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IValueMethods extends IPluginMethodMap {
  updateValue(args: any): Promise<any>;
  createValue(args: any): Promise<any>;
  deleteValue(args: any): Promise<any>;
}

export class ValueService implements IAgentPlugin {
  readonly methods: IValueMethods;

  constructor() {
    this.methods = {
      updateValue: this.updateValue.bind(this),
      createValue: this.createValue.bind(this),
      deleteValue: this.deleteValue.bind(this),
    };
  }

  async uploadFile(arr: any, formData: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let imagesUrlArray: any = [];
    let fileType = "jpeg";
    if (arr && is.not.empty(arr)) {
      for (let obj of arr) {
        if (obj && is.existy(obj)) {
          if (this.isValidHttpUrl(obj)) {
            imagesUrlArray.push(obj);
            continue;
          }
          fileType = this.getFileTypeFromBase64(obj) ?? "jpeg";
          try {
            if (client) {
              const res = await client.mutate({
                mutation: UPLOAD_FILE,
                variables: {
                  name: fileType,
                  data: obj,
                  category1: "costumevalues",
                  category2: formData?.name ?? "costumeImg",
                  addBandName: true,
                },
                fetchPolicy: "no-cache",
              });
              if (res?.data?.uploadFile) {
                imagesUrlArray.push(res?.data?.uploadFile);
              }
            }
          } catch (err: any) {
            let _display_message =
              err?.graphQLErrors &&
              Array.isArray(err?.graphQLErrors) &&
              err?.graphQLErrors.find((item: any) => item.extensions);
            if (_display_message?.extensions?.display_message) {
              throw Error(_display_message?.extensions?.display_message);
            }

            throw err;
          }
        }
      }
    }

    return imagesUrlArray;
  }

  async createValue(args: CreateValueProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);
        const imagesArr: [] = await this.uploadFile(args?.images, args);

        const res = await client.mutate({
          mutation: CREATE_VALUE,
          variables: {
            data: {
              ...args,
              images: imagesArr,
              name: args?.name?.trim(),
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.createCostumeValue,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateValue(args: UpdateValueProps) {
    log.blue(`inviteUser server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        log.blue(`inviteUser server component222:`, args);
        const imagesArr: [] = await this.uploadFile(
          args?.formData?.images,
          args?.formData
        );

        const res = await client.mutate({
          mutation: UPDATE_VALUE,
          variables: {
            filter: {
              _id: args?.id,
            },
            update: {
              ...args?.formData,
              images: imagesArr,
              name: args?.formData?.name?.trim(),
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.updateCostumeValue,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async deleteValue(args: DeleteValueProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_VALUE,
          variables: {
            filter: {
              _id: args?.id,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private isValidHttpUrl(str: string) {
    let url;
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    if (urlRegex.test(str)) {
      return true;
    }
    return false;
  }

  private getFileTypeFromBase64(base64String: string) {
    const matches = base64String.match(/^data:image\/(\w+)/);
    if (matches && matches.length === 2) {
      return matches[1];
    }
    return null; // Invalid base64 string or unable to determine the file type
  }
}
